// Security utilities for GDPR compliance and protection
// Implements security best practices for child data protection

import { ValidationResult } from '../types';

// GDPR Data Minimization - only collect necessary data
export const GDPR_COMPLIANT_FIELDS = {
  child: ['name', 'age', 'avatar_color', 'total_points', 'current_level'],
  preferences: ['theme_mode', 'high_contrast', 'calm_colors', 'sound_enabled'],
  progress: ['score', 'completed_at', 'time_spent', 'points_earned']
} as const;

// Content Security Policy headers for XSS protection
export const CSP_HEADER = {
  'Content-Security-Policy': 
    "default-src 'self'; " +
    "script-src 'self' 'unsafe-inline' https://router.requesty.ai; " +
    "style-src 'self' 'unsafe-inline' https://fonts.googleapis.com; " +
    "font-src 'self' https://fonts.gstatic.com; " +
    "img-src 'self' data: https:; " +
    "connect-src 'self' https://*.supabase.co https://router.requesty.ai; " +
    "frame-src 'none'; " +
    "object-src 'none'; " +
    "base-uri 'self';"
};

// CORS headers for API protection
export const CORS_HEADERS = {
  'Access-Control-Allow-Origin': process.env.NODE_ENV === 'production' 
    ? 'https://neurocademy.app' 
    : 'http://localhost:5173',
  'Access-Control-Allow-Headers': 'authorization, x-client-info, apikey, content-type',
  'Access-Control-Allow-Methods': 'GET, POST, PUT, DELETE, OPTIONS',
  'Access-Control-Allow-Credentials': 'true'
};

// Input sanitization for preventing injection attacks
export function sanitizeSQL(input: string): string {
  if (typeof input !== 'string') return '';
  
  // Remove or escape SQL injection patterns
  return input
    .replace(/['";\\]/g, '') // Remove dangerous SQL characters
    .replace(/(-{2}|\/\*|\*\/)/g, '') // Remove SQL comment patterns
    .replace(/\b(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|EXEC|UNION)\b/gi, '') // Remove SQL keywords
    .trim();
}

// Validate API requests to prevent CSRF
export function validateAPIRequest(request: Request, expectedOrigin?: string): ValidationResult<boolean> {
  const errors: Array<{field: string; message: string; code: string}> = [];
  
  // Check Origin header for CSRF protection
  const origin = request.headers.get('origin');
  if (expectedOrigin && origin !== expectedOrigin) {
    errors.push({
      field: 'origin',
      message: 'Request origin not allowed',
      code: 'INVALID_ORIGIN'
    });
  }
  
  // Check Content-Type for POST requests
  if (request.method === 'POST') {
    const contentType = request.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      errors.push({
        field: 'content-type',
        message: 'Content-Type must be application/json',
        code: 'INVALID_CONTENT_TYPE'
      });
    }
  }
  
  // Check for required authentication header
  const authHeader = request.headers.get('authorization');
  if (!authHeader || !authHeader.startsWith('Bearer ')) {
    errors.push({
      field: 'authorization',
      message: 'Valid authorization header required',
      code: 'MISSING_AUTH'
    });
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
}

// Generate secure random tokens for session management
export function generateSecureToken(length: number = 32): string {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  
  // Use crypto API if available, fallback to Math.random (less secure)
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const array = new Uint8Array(length);
    crypto.getRandomValues(array);
    for (let i = 0; i < length; i++) {
      result += charset[array[i] % charset.length];
    }
  } else {
    for (let i = 0; i < length; i++) {
      result += charset.charAt(Math.floor(Math.random() * charset.length));
    }
  }
  
  return result;
}

// GDPR Compliance: Data retention policy
export const DATA_RETENTION_POLICY = {
  CHILD_PROFILES: 365 * 24 * 60 * 60 * 1000, // 1 year in milliseconds
  ACTIVITY_LOGS: 90 * 24 * 60 * 60 * 1000,   // 90 days
  SESSION_DATA: 24 * 60 * 60 * 1000,         // 24 hours
  AI_ACTIVITIES: 30 * 24 * 60 * 60 * 1000    // 30 days
};

// Check if data should be archived/deleted based on retention policy
export function shouldArchiveData(createdAt: string, retentionPeriod: number): boolean {
  const created = new Date(createdAt).getTime();
  const now = Date.now();
  return (now - created) > retentionPeriod;
}

// GDPR: Data export for user rights
export function exportUserData(userId: string, childrenData: Record<string, unknown>[], progressData: Record<string, unknown>[]): object {
  return {
    export_date: new Date().toISOString(),
    user_id: userId,
    data_types: ['children_profiles', 'learning_progress', 'achievements'],
    children: childrenData.map(child => ({
      name: child.name,
      age: child.age,
      total_points: child.total_points,
      current_level: child.current_level,
      created_at: child.created_at
    })),
    progress: progressData.map(progress => ({
      child_id: progress.child_id,
      activity_type: progress.activity_type,
      score: progress.score,
      completed_at: progress.completed_at,
      points_earned: progress.points_earned
    })),
    retention_policy: DATA_RETENTION_POLICY,
    contact_info: 'For data deletion requests, contact: privacy@neurocademy.app'
  };
}

// Audit logging for GDPR compliance
export interface AuditLog {
  timestamp: string;
  user_id: string;
  action: string;
  resource: string;
  resource_id: string;
  success: boolean;
  ip_address?: string;
  user_agent?: string;
}

// Secure audit log creation with admin verification
export async function createSecureAuditLog(
  userId: string,
  action: string,
  resource: string,
  resourceId: string,
  success: boolean,
  request?: Request,
  supabaseClient?: unknown
): Promise<{ success: boolean; logId?: string; error?: string }> {
  try {
    if (!supabaseClient) {
      return { success: false, error: 'Database client not available' };
    }
    
    // For admin actions, verify admin privileges
    if (action.startsWith('admin_') || action.includes('grant_') || action.includes('revoke_')) {
      const { data: adminCheck } = await supabaseClient
        .rpc('check_admin_role', { user_id: userId, required_role: 'admin' });
      
      if (!adminCheck) {
        return { success: false, error: 'Admin privileges required for this action' };
      }
    }
    
    const auditLog = createAuditLog(userId, action, resource, resourceId, success, request);
    
    // Insert audit log using the secure function
    const { data, error } = await supabaseClient
      .rpc('log_admin_action', {
        action: auditLog.action,
        target_user_id: resourceId === userId ? null : resourceId,
        details: {
          resource: auditLog.resource,
          ip_address: auditLog.ip_address,
          user_agent: auditLog.user_agent,
          timestamp: auditLog.timestamp
        },
        ip_address: auditLog.ip_address,
        user_agent: auditLog.user_agent
      });
    
    if (error) {
      console.error('Failed to create audit log:', error);
      return { success: false, error: error.message };
    }
    
    return { success: true, logId: data };
  } catch {
    console.error('Audit log creation failed:', error);
    return { success: false, error: 'Failed to create audit log' };
  }
}

// Function to retrieve audit logs (admin only)
export async function getAuditLogs(
  supabaseClient: unknown,
  userId: string,
  filters?: {
    startDate?: string;
    endDate?: string;
    action?: string;
    targetUserId?: string;
    limit?: number;
  }
): Promise<{ success: boolean; logs?: Record<string, unknown>[]; error?: string }> {
  try {
    // Check if user has admin privileges
    const { data: adminCheck } = await supabaseClient
      .rpc('check_admin_role', { user_id: userId, required_role: 'admin' });
    
    if (!adminCheck) {
      return { success: false, error: 'Admin privileges required to view audit logs' };
    }
    
    // Build query for audit logs
    let query = supabaseClient
      .from('admin_audit_log')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (filters?.startDate) {
      query = query.gte('created_at', filters.startDate);
    }
    
    if (filters?.endDate) {
      query = query.lte('created_at', filters.endDate);
    }
    
    if (filters?.action) {
      query = query.eq('action', filters.action);
    }
    
    if (filters?.targetUserId) {
      query = query.eq('target_user_id', filters.targetUserId);
    }
    
    if (filters?.limit) {
      query = query.limit(filters.limit);
    } else {
      query = query.limit(100); // Default limit
    }
    
    const { data, error } = await query;
    
    if (error) {
      return { success: false, error: error.message };
    }
    
    return { success: true, logs: data };
  } catch {
    console.error('Failed to retrieve audit logs:', error);
    return { success: false, error: 'Failed to retrieve audit logs' };
  }
}

export function createAuditLog(
  userId: string,
  action: string,
  resource: string,
  resourceId: string,
  success: boolean,
  request?: Request
): AuditLog {
  return {
    timestamp: new Date().toISOString(),
    user_id: userId,
    action,
    resource,
    resource_id: resourceId,
    success,
    ip_address: request?.headers.get('x-forwarded-for') || request?.headers.get('x-real-ip') || 'unknown',
    user_agent: request?.headers.get('user-agent') || 'unknown'
  };
}

// Security headers for API responses
export function addSecurityHeaders(response: Response): Response {
  const headers = new Headers(response.headers);
  
  // Prevent XSS
  headers.set('X-Content-Type-Options', 'nosniff');
  headers.set('X-Frame-Options', 'DENY');
  headers.set('X-XSS-Protection', '1; mode=block');
  
  // HSTS for HTTPS enforcement
  headers.set('Strict-Transport-Security', 'max-age=31536000; includeSubDomains');
  
  // Prevent information leakage
  headers.set('Referrer-Policy', 'strict-origin-when-cross-origin');
  
  // CSP header
  headers.set('Content-Security-Policy', CSP_HEADER['Content-Security-Policy']);
  
  return new Response(response.body, {
    status: response.status,
    statusText: response.statusText,
    headers
  });
}

// Validate child ownership (prevent unauthorized access)
export async function validateChildOwnership(
  childId: string, 
  parentId: string, 
  supabaseClient: unknown
): Promise<ValidationResult<boolean>> {
  try {
    const { data, error } = await supabaseClient
      .from('children')
      .select('parent_id')
      .eq('id', childId)
      .single();
    
    if (error) {
      return {
        isValid: false,
        errors: [{
          field: 'child_id',
          message: 'Child not found',
          code: 'NOT_FOUND'
        }]
      };
    }
    
    if (data.parent_id !== parentId) {
      return {
        isValid: false,
        errors: [{
          field: 'child_id',
          message: 'Access denied: Not authorized to access this child',
          code: 'UNAUTHORIZED'
        }]
      };
    }
    
    return {
      isValid: true,
      data: true,
      errors: []
    };
  } catch {
    return {
      isValid: false,
      errors: [{
        field: 'validation',
        message: 'Database error during validation',
        code: 'DB_ERROR'
      }]
    };
  }
}

// Content filtering for AI-generated activities (child safety)
export function filterInappropriateContent(content: string): { isAppropriate: boolean; filteredContent: string } {
  // List of inappropriate words/phrases for children's content
  const inappropriatePatterns = [
    /\b(violence|weapon|gun|knife|kill|death|blood|fight|war|hate|stupid|dumb|idiot)\b/gi,
    /\b(scary|frightening|terrifying|horror|nightmare)\b/gi,
    // Add more patterns as needed for child safety
  ];
  
  let isAppropriate = true;
  let filteredContent = content;
  
  inappropriatePatterns.forEach(pattern => {
    if (pattern.test(content)) {
      isAppropriate = false;
      filteredContent = filteredContent.replace(pattern, '[content filtered]');
    }
  });
  
  return { isAppropriate, filteredContent };
}

// Secure encryption/decryption utilities for PII data
interface EncryptionResult {
  encrypted: string;
  iv: string;
  salt: string;
}

// Generate a cryptographically secure key from password/passphrase
async function deriveKey(password: string, salt: Uint8Array): Promise<CryptoKey> {
  const encoder = new TextEncoder();
  const passwordBuffer = encoder.encode(password);
  
  // Import the password as a key for PBKDF2
  const baseKey = await crypto.subtle.importKey(
    'raw',
    passwordBuffer,
    'PBKDF2',
    false,
    ['deriveKey']
  );
  
  // Derive a key using PBKDF2
  return await crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt,
      iterations: 100000, // High iteration count for security
      hash: 'SHA-256'
    },
    baseKey,
    {
      name: 'AES-GCM',
      length: 256
    },
    false,
    ['encrypt', 'decrypt']
  );
}

// Encrypt sensitive data using AES-GCM
export async function encryptSensitiveData(
  data: string, 
  password: string = 'default-encryption-key'
): Promise<EncryptionResult | string> {
  if (!crypto || !crypto.subtle) {
    // Fallback for environments without Web Crypto API
    console.warn('Web Crypto API not available, using base64 encoding (NOT SECURE)');
    return btoa(data);
  }
  
  try {
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(data);
    
    // Generate random salt and IV
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const iv = crypto.getRandomValues(new Uint8Array(12)); // 96-bit IV for AES-GCM
    
    // Derive encryption key
    const key = await deriveKey(password, salt);
    
    // Encrypt the data
    const encryptedBuffer = await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: iv
      },
      key,
      dataBuffer
    );
    
    // Convert to base64 strings for storage
    const encrypted = btoa(String.fromCharCode(...new Uint8Array(encryptedBuffer)));
    const ivBase64 = btoa(String.fromCharCode(...iv));
    const saltBase64 = btoa(String.fromCharCode(...salt));
    
    return {
      encrypted,
      iv: ivBase64,
      salt: saltBase64
    };
  } catch {
    console.error('Encryption failed:', error);
    // Fallback to base64 encoding
    return btoa(data);
  }
}

// Decrypt sensitive data using AES-GCM
export async function decryptSensitiveData(
  encryptedData: EncryptionResult | string,
  password: string = 'default-encryption-key'
): Promise<string | null> {
  if (!crypto || !crypto.subtle) {
    // Fallback for environments without Web Crypto API
    if (typeof encryptedData === 'string') {
      try {
        return atob(encryptedData);
      } catch {
        return null;
      }
    }
    return null;
  }
  
  // Handle fallback case where data was base64 encoded
  if (typeof encryptedData === 'string') {
    try {
      return atob(encryptedData);
    } catch {
      return null;
    }
  }
  
  try {
    // Convert base64 strings back to arrays
    const encryptedBuffer = new Uint8Array(
      atob(encryptedData.encrypted).split('').map(char => char.charCodeAt(0))
    );
    const iv = new Uint8Array(
      atob(encryptedData.iv).split('').map(char => char.charCodeAt(0))
    );
    const salt = new Uint8Array(
      atob(encryptedData.salt).split('').map(char => char.charCodeAt(0))
    );
    
    // Derive the same key used for encryption
    const key = await deriveKey(password, salt);
    
    // Decrypt the data
    const decryptedBuffer = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: iv
      },
      key,
      encryptedBuffer
    );
    
    // Convert back to string
    const decoder = new TextDecoder();
    return decoder.decode(decryptedBuffer);
  } catch {
    console.error('Decryption failed:', error);
    return null;
  }
}

// Secure hash function for non-reversible data (like generating IDs)
export async function secureHash(data: string, algorithm: 'SHA-256' | 'SHA-384' | 'SHA-512' = 'SHA-256'): Promise<string> {
  if (!crypto || !crypto.subtle) {
    // Fallback using a simple hash (NOT CRYPTOGRAPHICALLY SECURE)
    let hash = 0;
    for (let i = 0; i < data.length; i++) {
      const char = data.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(16);
  }
  
  try {
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(data);
    const hashBuffer = await crypto.subtle.digest(algorithm, dataBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  } catch {
    console.error('Hashing failed:', error);
    return '';
  }
}

// Utility to check if encryption is available
export function isEncryptionAvailable(): boolean {
  return !!(crypto && crypto.subtle);
}

// Secure data storage wrapper
export class SecureDataStore {
  private keyPrefix: string;
  private encryptionPassword: string;
  
  constructor(keyPrefix: string = 'neurocademy', encryptionPassword?: string) {
    this.keyPrefix = keyPrefix;
    this.encryptionPassword = encryptionPassword || this.generateDefaultPassword();
  }
  
  private generateDefaultPassword(): string {
    // In production, this should come from environment variables or secure key management
    return `${window.location.origin}-encryption-key-${Date.now()}`;
  }
  
  private getStorageKey(key: string): string {
    return `${this.keyPrefix}_${key}`;
  }
  
  // Store encrypted data
  async setSecure(key: string, data: unknown): Promise<boolean> {
    try {
      const jsonData = JSON.stringify(data);
      const encrypted = await encryptSensitiveData(jsonData, this.encryptionPassword);
      
      if (typeof encrypted === 'string') {
        // Fallback storage
        localStorage.setItem(this.getStorageKey(key), encrypted);
      } else {
        // Secure storage
        localStorage.setItem(this.getStorageKey(key), JSON.stringify(encrypted));
      }
      
      return true;
    } catch {
      console.error('Secure storage failed:', error);
      return false;
    }
  }
  
  // Retrieve encrypted data
  async getSecure(key: string): Promise<unknown> {
    try {
      const stored = localStorage.getItem(this.getStorageKey(key));
      if (!stored) return null;
      
      let encryptedData: EncryptionResult | string;
      
      try {
        // Try to parse as JSON (secure format)
        encryptedData = JSON.parse(stored);
      } catch {
        // Fallback format (base64 string)
        encryptedData = stored;
      }
      
      const decrypted = await decryptSensitiveData(encryptedData, this.encryptionPassword);
      if (!decrypted) return null;
      
      return JSON.parse(decrypted);
    } catch {
      console.error('Secure retrieval failed:', error);
      return null;
    }
  }
  
  // Remove secure data
  remove(key: string): void {
    localStorage.removeItem(this.getStorageKey(key));
  }
  
  // Clear all secure data with this prefix
  clear(): void {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith(this.keyPrefix)) {
        localStorage.removeItem(key);
      }
    });
  }
}

// Default secure store instance
export const secureStore = new SecureDataStore();

// Utility for field-level encryption in forms
export async function encryptFormField(
  fieldValue: string, 
  fieldName: string
): Promise<{ encrypted: string; isEncrypted: boolean }> {
  // Only encrypt sensitive fields
  const sensitiveFields = [
    'email', 'phone', 'address', 'full_name', 'ssn', 'credit_card'
  ];
  
  const shouldEncrypt = sensitiveFields.some(field => 
    fieldName.toLowerCase().includes(field)
  );
  
  if (!shouldEncrypt || !fieldValue.trim()) {
    return { encrypted: fieldValue, isEncrypted: false };
  }
  
  const encrypted = await encryptSensitiveData(fieldValue);
  
  return {
    encrypted: typeof encrypted === 'string' ? encrypted : JSON.stringify(encrypted),
    isEncrypted: typeof encrypted === 'object'
  };
}

// Utility for field-level decryption in forms
export async function decryptFormField(
  encryptedValue: string, 
  isEncrypted: boolean
): Promise<string> {
  if (!isEncrypted) {
    return encryptedValue;
  }
  
  let encryptedData: EncryptionResult | string;
  
  try {
    encryptedData = JSON.parse(encryptedValue);
  } catch {
    encryptedData = encryptedValue;
  }
  
  const decrypted = await decryptSensitiveData(encryptedData);
  return decrypted || encryptedValue;
}

// Privacy-compliant error messages (don't leak sensitive info)
export function createSafeErrorMessage(error: unknown, userFacing: boolean = true): string {
  if (userFacing) {
    // Generic user-facing messages to avoid information leakage
    const safeMessages: { [key: string]: string } = {
      'UNAUTHORIZED': 'You do not have permission to access this resource.',
      'NOT_FOUND': 'The requested resource was not found.',
      'VALIDATION_ERROR': 'The provided information is not valid.',
      'RATE_LIMIT': 'Too many requests. Please try again later.',
      'SERVER_ERROR': 'We are experiencing technical difficulties. Please try again.'
    };
    
    return safeMessages[error.code] || safeMessages['SERVER_ERROR'];
  } else {
    // Detailed error for logging/debugging (internal use only)
    return `${error.code}: ${error.message} - ${error.details || ''}`;
  }
}